import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  Youtube,
  MoreHorizontal,
  Edit2,
  Trash2,
  Eye
} from 'lucide-react';

interface CalendarPost {
  id: string;
  content: string;
  platforms: string[];
  scheduled_at: string;
  status: 'draft' | 'scheduled' | 'published' | 'failed' | 'partial';
  media_url?: string;
}

interface ContentCalendarProps {
  posts?: CalendarPost[];
  loading?: boolean;
  onPostClick?: (post: CalendarPost) => void;
  onEditPost?: (post: CalendarPost) => void;
  onDeletePost?: (id: string) => void;
  onCreatePost?: (date: Date) => void;
}

const platformIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
  youtube: Youtube
};

const platformColors: Record<string, string> = {
  instagram: 'bg-gradient-to-br from-purple-500 to-pink-500',
  facebook: 'bg-blue-600',
  twitter: 'bg-gray-900 dark:bg-white dark:text-gray-900',
  linkedin: 'bg-blue-700',
  youtube: 'bg-red-600'
};

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700',
  scheduled: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  published: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
  failed: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
  partial: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
};

const ContentCalendar: React.FC<ContentCalendarProps> = ({
  posts = [],
  loading = false,
  onPostClick,
  onEditPost,
  onDeletePost,
  onCreatePost
}) => {
  const { t } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>(
    () => window.innerWidth < 768 ? 'day' : 'month'
  );
  const [selectedPost, setSelectedPost] = useState<string | null>(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Generate week days for week view
  const weekDaysArray = useMemo(() => {
    const startOfWeek = new Date(currentDate);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day);

    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      days.push(d);
    }
    return days;
  }, [currentDate]);

  // Hours for day/week view
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startDay = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    const days: (Date | null)[] = [];

    // Add empty cells for days before the first day
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    // Add empty cells to complete the last week
    const remainingDays = 7 - (days.length % 7);
    if (remainingDays < 7) {
      for (let i = 0; i < remainingDays; i++) {
        days.push(null);
      }
    }

    return days;
  }, [year, month]);

  // Get posts for a specific date
  const getPostsForDate = (date: Date | null): CalendarPost[] => {
    if (!date) return [];
    return posts.filter(post => {
      const postDate = new Date(post.scheduled_at);
      return postDate.toDateString() === date.toDateString();
    });
  };

  // Navigation
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get posts for a specific hour on a specific date (for day/week view)
  const getPostsForHour = (date: Date, hour: number): CalendarPost[] => {
    return posts.filter(post => {
      const postDate = new Date(post.scheduled_at);
      return postDate.toDateString() === date.toDateString() && postDate.getHours() === hour;
    });
  };

  // Get header title based on view mode
  const getHeaderTitle = (): string => {
    if (viewMode === 'month') {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else if (viewMode === 'week') {
      const startOfWeek = weekDaysArray[0];
      const endOfWeek = weekDaysArray[6];
      if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
        return `${startOfWeek.getDate()} - ${endOfWeek.getDate()} ${monthNames[startOfWeek.getMonth()]} ${startOfWeek.getFullYear()}`;
      }
      return `${startOfWeek.getDate()} ${monthNames[startOfWeek.getMonth()]} - ${endOfWeek.getDate()} ${monthNames[endOfWeek.getMonth()]} ${endOfWeek.getFullYear()}`;
    } else {
      return `${currentDate.getDate()} ${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
  };

  // Check if date is today
  const isToday = (date: Date | null): boolean => {
    if (!date) return false;
    return date.toDateString() === new Date().toDateString();
  };

  // Check if date is in the past
  const isPast = (date: Date | null): boolean => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const formatTime = (dateString: string): string => {
    return new Date(dateString).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const weekDays = t('social.daysShort', { returnObjects: true, defaultValue: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] }) as string[];
  const monthNames = t('social.months', { returnObjects: true, defaultValue: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] }) as string[];

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <div className="flex gap-2">
            <div className="h-10 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="bg-gray-50 dark:bg-gray-900 h-24 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white capitalize">
            {getHeaderTitle()}
          </h2>
          <div className="flex items-center gap-1">
            <button
              onClick={goToPrevious}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <button
              onClick={goToNext}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={goToToday}
            className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
          >
            {t('calendar.today', 'Hoje')}
          </button>
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('day')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === 'day'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {t('calendar.day', 'Dia')}
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === 'week'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {t('calendar.week', 'Semana')}
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                viewMode === 'month'
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {t('calendar.month', 'Mês')}
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        {/* MONTH VIEW */}
        {viewMode === 'month' && (
          <>
            {/* Week Days Header */}
            <div className="grid grid-cols-7 gap-px mb-2">
              {weekDays.map((day) => (
                <div
                  key={day}
                  className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden">
              {calendarDays.map((date, index) => {
                const dayPosts = getPostsForDate(date);
                const isCurrentDay = isToday(date);
                const isPastDay = isPast(date);

                return (
                  <div
                    key={index}
                    className={`
                      min-h-[100px] p-2 bg-white dark:bg-gray-900 transition-colors group
                      ${!date ? 'bg-gray-50 dark:bg-gray-900/50' : ''}
                      ${isCurrentDay ? 'ring-2 ring-primary-500 ring-inset' : ''}
                      ${isPastDay && date ? 'opacity-60' : ''}
                    `}
                  >
                    {date && (
                      <>
                        <div className="flex items-center justify-between mb-1">
                          <span
                            className={`
                              text-sm font-medium
                              ${isCurrentDay
                                ? 'w-7 h-7 rounded-full bg-primary-500 text-white flex items-center justify-center'
                                : 'text-gray-900 dark:text-white'
                              }
                            `}
                          >
                            {date.getDate()}
                          </span>
                          {!isPastDay && (
                            <button
                              onClick={() => onCreatePost?.(date)}
                              className="p-1 rounded text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          )}
                        </div>

                        <div className="space-y-1">
                          {dayPosts.slice(0, 3).map((post) => (
                            <div
                              key={post.id}
                              onClick={() => onPostClick?.(post)}
                              className={`
                                relative group/post text-xs p-1.5 rounded border cursor-pointer
                                transition-all hover:shadow-sm
                                ${statusColors[post.status]}
                              `}
                            >
                              <div className="flex items-center gap-1 mb-0.5">
                                {post.platforms.slice(0, 2).map((platform) => {
                                  const Icon = platformIcons[platform];
                                  return Icon ? (
                                    <div
                                      key={platform}
                                      className={`w-4 h-4 rounded-full flex items-center justify-center text-white ${platformColors[platform]}`}
                                    >
                                      <Icon className="h-2.5 w-2.5" />
                                    </div>
                                  ) : null;
                                })}
                                {post.platforms.length > 2 && (
                                  <span className="text-[10px] text-gray-500">
                                    +{post.platforms.length - 2}
                                  </span>
                                )}
                                <span className="ml-auto text-[10px] text-gray-400 flex items-center gap-0.5">
                                  <Clock className="h-2.5 w-2.5" />
                                  {formatTime(post.scheduled_at)}
                                </span>
                              </div>
                              <p className="truncate font-medium">
                                {post.content.substring(0, 30)}...
                              </p>

                              {/* Hover Actions */}
                              <div className="absolute right-1 top-1 hidden group-hover/post:flex items-center gap-0.5 bg-white dark:bg-gray-800 rounded shadow-sm p-0.5">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEditPost?.(post);
                                  }}
                                  className="p-1 text-gray-400 hover:text-primary-600"
                                >
                                  <Edit2 className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDeletePost?.(post.id);
                                  }}
                                  className="p-1 text-gray-400 hover:text-red-600"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                          {dayPosts.length > 3 && (
                            <button className="w-full text-[10px] text-primary-600 dark:text-primary-400 hover:underline text-center">
                              +{dayPosts.length - 3} {t('common.more', 'mais')}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* WEEK VIEW */}
        {viewMode === 'week' && (
          <>
            {/* Week Days Header */}
            <div className="grid grid-cols-8 gap-px mb-2">
              <div className="w-16" /> {/* Empty space for time column */}
              {weekDaysArray.map((date) => (
                <div
                  key={date.toISOString()}
                  className={`text-center py-2 ${isToday(date) ? 'bg-primary-50 dark:bg-primary-900/20 rounded-lg' : ''}`}
                >
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {date.toLocaleDateString(undefined, { weekday: 'short' })}
                  </div>
                  <div className={`text-lg font-semibold ${isToday(date) ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-white'}`}>
                    {date.getDate()}
                  </div>
                </div>
              ))}
            </div>

            {/* Time Grid */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-[600px] overflow-y-auto">
              {hours.filter(h => h >= 6 && h <= 22).map((hour) => (
                <div key={hour} className="grid grid-cols-8 gap-px bg-gray-200 dark:bg-gray-700">
                  {/* Time Label */}
                  <div className="w-16 py-3 px-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 text-right">
                    {String(hour).padStart(2, '0')}:00
                  </div>
                  {/* Day Cells */}
                  {weekDaysArray.map((date) => {
                    const hourPosts = getPostsForHour(date, hour);
                    const isCurrentDay = isToday(date);

                    return (
                      <div
                        key={`${date.toISOString()}-${hour}`}
                        className={`
                          min-h-[50px] p-1 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800
                          hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group
                          ${isCurrentDay ? 'bg-primary-50/30 dark:bg-primary-900/10' : ''}
                        `}
                      >
                        {hourPosts.map((post) => (
                          <div
                            key={post.id}
                            onClick={() => onPostClick?.(post)}
                            className={`
                              text-xs p-1.5 rounded border cursor-pointer mb-1
                              transition-all hover:shadow-sm
                              ${statusColors[post.status]}
                            `}
                          >
                            <div className="flex items-center gap-1 mb-0.5">
                              {post.platforms.slice(0, 2).map((platform) => {
                                const Icon = platformIcons[platform];
                                return Icon ? (
                                  <div
                                    key={platform}
                                    className={`w-3 h-3 rounded-full flex items-center justify-center text-white ${platformColors[platform]}`}
                                  >
                                    <Icon className="h-2 w-2" />
                                  </div>
                                ) : null;
                              })}
                              <span className="text-[10px] text-gray-400">
                                {formatTime(post.scheduled_at)}
                              </span>
                            </div>
                            <p className="truncate font-medium text-[10px]">
                              {post.content.substring(0, 20)}...
                            </p>
                          </div>
                        ))}
                        {hourPosts.length === 0 && (
                          <button
                            onClick={() => {
                              const newDate = new Date(date);
                              newDate.setHours(hour, 0, 0, 0);
                              onCreatePost?.(newDate);
                            }}
                            className="w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Plus className="h-4 w-4 text-gray-400" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        )}

        {/* DAY VIEW */}
        {viewMode === 'day' && (
          <>
            {/* Day Header */}
            <div className="text-center py-4 mb-4 border-b border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {currentDate.toLocaleDateString(undefined, { weekday: 'long' })}
              </div>
              <div className={`text-3xl font-bold ${isToday(currentDate) ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-white'}`}>
                {currentDate.getDate()}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
              </div>
            </div>

            {/* Time Grid */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-[600px] overflow-y-auto">
              {hours.filter(h => h >= 6 && h <= 22).map((hour) => {
                const hourPosts = getPostsForHour(currentDate, hour);

                return (
                  <div
                    key={hour}
                    className={`
                      flex gap-4 p-3 border-b border-gray-100 dark:border-gray-800
                      hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group
                      ${isPast(new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), hour)) ? 'opacity-60' : ''}
                    `}
                  >
                    {/* Time Label */}
                    <div className="w-16 text-sm text-gray-500 dark:text-gray-400 font-medium">
                      {String(hour).padStart(2, '0')}:00
                    </div>

                    {/* Posts */}
                    <div className="flex-1 space-y-2">
                      {hourPosts.map((post) => (
                        <div
                          key={post.id}
                          onClick={() => onPostClick?.(post)}
                          className={`
                            relative group/post p-3 rounded-lg border cursor-pointer
                            transition-all hover:shadow-md
                            ${statusColors[post.status]}
                          `}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            {post.platforms.map((platform) => {
                              const Icon = platformIcons[platform];
                              return Icon ? (
                                <div
                                  key={platform}
                                  className={`w-6 h-6 rounded-full flex items-center justify-center text-white ${platformColors[platform]}`}
                                >
                                  <Icon className="h-3 w-3" />
                                </div>
                              ) : null;
                            })}
                            <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTime(post.scheduled_at)}
                            </span>
                            <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
                              post.status === 'published' ? 'bg-green-100 text-green-700' :
                              post.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                              post.status === 'failed' ? 'bg-red-100 text-red-700' :
                              post.status === 'partial' ? 'bg-amber-100 text-amber-800' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {t(`social.status.${post.status}`, post.status)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {post.content}
                          </p>

                          {/* Hover Actions */}
                          <div className="absolute right-2 top-2 hidden group-hover/post:flex items-center gap-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditPost?.(post);
                              }}
                              className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeletePost?.(post.id);
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {hourPosts.length === 0 && (
                        <button
                          onClick={() => {
                            const newDate = new Date(currentDate);
                            newDate.setHours(hour, 0, 0, 0);
                            onCreatePost?.(newDate);
                          }}
                          className="w-full py-2 text-gray-400 hover:text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          <span className="text-sm">{t('social.addPost', 'Adicionar post')}</span>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
        {Object.entries(statusColors).map(([status, colorClass]) => (
          <div key={status} className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded ${colorClass.split(' ')[0]}`} />
            <span className="text-xs text-gray-600 dark:text-gray-400 capitalize">
              {t(`social.status.${status}`, status)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContentCalendar;
